import type { Translation } from '../types';

export const ja: Translation = {
  meta: { name: '日本語', english: 'Japanese', tag: 'ja', dir: 'ltr' },

  common: {
    menu: "メニュー",
    cancel: 'キャンセル',
    done: '完了',
    join: '参加',
    leave: '退出',
    refresh: '更新',
    save: '保存',
    you: 'あなた',
    host: 'ホスト',
    ai: 'AI',
    ready: '準備完了',
    notReady: '未準備',
  },

  conn: {
    live: '接続中',
    connecting: '接続しています',
    offline: 'オフライン',
    connectingFull: '接続しています…',
    reconnectBanner: '接続が切れました。再接続しています。席は3分間確保されます。',
  },

  nickname: {
    pickTitle: '名前を決めましょう',
    changeTitle: '名前を変更',
    subtitle: '他のプレイヤーにはこの名前で表示されます。',
    placeholder: '例：ワイルド4',
    label: 'ニックネーム',
    letsPlay: 'はじめる',
  },

  lobby: {
    heroTitle: '友だちとUNOで遊ぼう',
    heroSubtitle: '登録不要。リンクを共有して配るだけ。',
    createRoom: 'ルームを作成',
    roomCode: 'ルームコード',
    publicRooms: '公開ルーム',
    empty: '公開ルームはまだありません。最初の1つを作りましょう。',
    cardMeta: 'ホスト {host} ・ 持ち時間 {seconds}秒',
    stacking: '重ね出し',
    badgeOpen: '参加可',
    badgeFull: '満室',
    badgeLive: 'プレイ中',
    lockedTitle: 'ルーム {id} はロックされています',
    password: 'パスワード',
    passwordLabel: 'ルームのパスワード',
  },

  create: {
    title: '新しいルーム',
    roomName: 'ルーム名',
    roomNamePlaceholder: '金曜夜のUNO',
    maxPlayers: '最大人数',
    playersOption: { other: '{count}人' },
    passwordOptional: 'パスワード（任意）',
    passwordPlaceholder: '空欄なら誰でも参加可',
    listPublic: '公開ルーム一覧に表示する',
    houseRules: 'ハウスルール',
    submit: '作成',
  },

  rules: {
    turnTimer: '持ち時間',
    turnTimerHint: '時間切れになると、ボットがあなたの代わりに出せるカードをランダムに出します。',
    startingHand: '初期手札',
    unoPenalty: 'UNOペナルティ',
    stackDrawTwo: '+2 の重ね出し',
    stackDrawTwoHint: '+2 に対して引かずに +2 を重ねられる',
    stackDrawFour: '+4 の重ね出し',
    stackDrawFourHint: '+4 に対して +4 を重ねられる',
    challengeWildFour: '+4 チャレンジ',
    challengeWildFourHint: '次のプレイヤーが不正な +4 を指摘できる',
    strictWildFour: '厳格な +4',
    strictWildFourHint: '場の色のカードを持っていないときだけ出せる',
    playDrawn: '引いたカードを出せる',
    playDrawnHint: '引いたカードをそのまま出せます',
    drawUntilPlayable: '出せるまで引く',
    drawUntilPlayableHint: '出せるカードが来るまで引き続けます',
    hostOnly: '変更できるのはホストだけです',
  },

  room: {
    copyLink: '招待リンクをコピー',
    copied: '✓ コピーしました',
    roomLabel: 'ルーム',
    locked: 'ロック中',
    public: '公開',
    unlisted: '非公開',
    readyStatus: '{total}人中 {ready}人が準備完了',
    botsJoining: { other: 'ボット{count}体が参加します' },
    startsWhenReady: '全員がそろった瞬間に開始します。',
    needMorePlayers: '開始には{min}人必要です。誰かを誘うか、ボットを追加してください。',
    playersTitle: 'プレイヤー',
    botsTitle: 'AIボット',
    botsHostOnly: '変更できるのはホストだけです',
    noBots: 'ボットなし',
    botsWillJoin: { other: 'ボット{count}体が参加' },
    botsHint: 'ゲーム開始時に着席します。現在は最大{max}体まで入れます。',
    botsRoomFull: 'ルームが満室のため、ボットを追加できません。',
    soloCta: 'ひとりで遊ぶ？ ボットを追加して開始',
    addBot: 'ボットを追加',
    removeBot: 'ボットを減らす',
    removePlayer: '{name} を退出させる',
    botPlaceholder: 'ボット {n}',
    defaultRoomName: '{name} のルーム',
    imReady: '準備完了',
    cancelReady: '準備完了を取り消す',
    offlineTag: 'オフライン',
  },

  game: {
    yourTurn: 'あなたの番',
    yourTurnStack: 'あなたの番 — 重ねるか +{count} を引く',
    othersTurn: '{name} の番',
    waiting: '待機中…',
    gameOver: 'ゲーム終了',
    challengeWindow: 'ワイルドドロー4 — チャレンジ受付中',
    drawPileLeft: '残り{count}枚',
    discard: '捨て札',
    draw: '引く',
    takeCards: '+{count} を引く',
    pass: 'パス',
    uno: 'UNO！',
    judge: '指摘',
    cardCount: { other: '{count}枚' },
    clockwise: '時計回り',
    counterClockwise: '反時計回り',
    activeColour: '現在の色',
    noCards: '手札なし — お見事。',
    oneCard: '残り1枚！',
    unoTag: 'UNO',
    forfeitedTag: 'リタイア',
    offlineFor: 'オフライン {seconds}秒',
    placeTag: '{place}位',
    pickColour: '色を選んでください',
    challengeTitle: '{name} がワイルドドロー4を出しました。チャレンジしますか？',
    challengeExplain: 'ブラフだった場合、相手が{bluff}枚引きます。違った場合はあなたが{fail}枚引きます。',
    challengeBtn: 'チャレンジ',
    acceptBtn: '+{count} を引く',
    youWin: '🎉 勝利！',
    standingsTitle: 'ゲーム終了',
    points: '{points}点',
    forfeited: 'リタイア',
    backToLobby: 'ロビーに戻る',
    playAgain: 'もう一度遊ぶ',
    waitingOthers: '他のプレイヤーを待っています…',
    faceDownCard: '裏向きのカード',
    sortByColour: "色で並べ替え",
    sortAsDealt: "配られた順のまま",
    drawACard: 'カードを1枚引く',
    drawNCards: 'カードを{count}枚引く',
  },

  events: {
    someone: '誰か',
    unoCalledYou: 'UNOを宣言しました！',
    unoCalledOther: '{name} がUNOを宣言！',
    judged: '{accuser} が {target} を指摘 — +{count}枚',
    judgedYou: '{accuser} に指摘されました — +{count}枚',
    challengeBluff: 'ブラフ発覚 — {name} が{count}枚引きます',
    challengeFailed: 'チャレンジ失敗 — {name} が{count}枚引きます',
    forfeited: '{name} がリタイアしました。',
    afkYou: '時間切れ — 自動でプレイしました。',
    afkOther: '{name} が時間切れ — 自動でプレイしました。',
    hostChanged: '{name} が新しいホストになりました。',
    reshuffled: '山札をシャッフルし直しました。',
    youWin: 'あなたの勝ち！',
    winner: '{name} の勝ち！',
    gameOver: 'ゲーム終了。',
  },

  errors: {
    bad_request: '問題が発生しました。',
    no_session: 'セッションが切れました。再接続しています。',
    room_not_found: 'そのルームは存在しません。',
    room_full: 'そのルームは満室です。',
    wrong_password: 'パスワードが違います。',
    already_in_room: '先に今のルームから退出してください。',
    not_in_room: 'ルームに参加していません。',
    not_host: 'ホストだけが実行できます。',
    game_in_progress: 'そのゲームはすでに始まっています。',
    not_enough_players: '人数が足りません。',
    stale_action: '他のプレイヤーが先に動いたため、操作は取り消されました。',
    illegal_action: 'その操作はできません。',
    rate_limited: '操作が速すぎます。',
    nickname_invalid: 'そのニックネームは使えません。',
  },

  theme: {
    title: 'カードテーマ',
    description: '画像がない場合は内蔵のCSSデッキが使われます。',
    builtIn: '内蔵 — 画像ファイル不要',
    pngHint: '300×450のPNGを {path} に置き、テーマ一覧に登録してください。',
  },

  language: {
    title: '言語',
    description: 'すぐに反映され、この端末に記憶されます。',
  },

  cards: {
    red: "赤",
    yellow: "黄",
    green: "緑",
    blue: "青",
    wild: "ワイルド",
    skip: "スキップ",
    reverse: "リバース",
    draw2: "+2",
    wildCard: "ワイルド",
    wild4: "+4",
  },

  log: {
    title: "プレイログ",
    empty: "まだ何もありません。",
    close: "ログを隠す",
    timestamps: "時刻を表示",
    chatPlaceholder: "何か話す…",
    send: "送信",
    played: "{name} が {card} を出しました",
    drew: "{name} が{count}枚引きました",
    skipped: "{name} は飛ばされました",
    reversed: "順番が逆になりました",
    joined: "{name} が参加しました",
    left: "{name} が退出しました",
    started: "ゲーム開始",
  },

  profile: {
    avatar: "アイコン",
    avatarHint: "アイコンを選ぶか、頭文字のままでもかまいません。",
  },

  leave: {
    title: "ゲームから抜けますか？",
    body: "まだ対局中です。抜けるとリタイア扱いになり、手札は山札に戻ります。",
    stay: "続ける",
    confirm: "抜ける",
    unload: "対局の途中です。",
  },
};
